import { CrossFrameUtils } from '@src/utils/CrossFrameUtils';
import { BluetoothLowEnergyNamespace } from './adapter/AbstractBluetoothLowEnergyAdapter';
import { BluetoothLowEnergyAdapterProvider } from './adapter/BluetoothLowEnergyAdapterProvider';

/**
 * One event pushed up from the radio.
 *
 * The `payload` shape is determined entirely by `type`. It is passed through this library verbatim,
 * so this list is the contract:
 *
 * | `type` | Role | `payload` |
 * |---|---|---|
 * | `adapterState` | adapter | `{ state }` |
 * | `scanResult` | central | `{ device: { address, name }, rssi, serviceUUIDs, connectable }` |
 * | `connectionState` | central | `{ deviceAddress, state }` |
 * | `characteristicValue` | central | `{ deviceAddress, serviceUUID, characteristicUUID, value }` |
 * | `readRequest` | peripheral | `{ requestId, deviceAddress, serviceUUID, characteristicUUID, offset }` |
 * | `writeRequest` | peripheral | `{ requestId, deviceAddress, serviceUUID, characteristicUUID, value, offset, responseNeeded }` |
 * | `centralConnection` | peripheral | `{ deviceAddress, state }` |
 * | `subscriptionState` | peripheral | `{ deviceAddress, serviceUUID, characteristicUUID, subscribed }` |
 * | `packetSize` | peripheral | `{ deviceAddress, packetSize }` |
 *
 * **`scanResult` is the odd one out: the peer is nested as `payload.device.address`, not
 * `payload.deviceAddress`.** Every other event that names a peer uses the flat form, so
 * `payload.deviceAddress` on a scan result is `undefined` rather than an error. `device.name` may be
 * `null`, as may `serviceUUIDs` and `connectable` — an advertisement is not required to carry them.
 *
 * Values (`value`) are Base64 in both directions. `state` is the token from the corresponding
 * operation's documented set.
 */
export interface IBluetoothLowEnergyEvent {
    /**
     * What happened. `adapterState` for the radio; `scanResult`, `connectionState` and
     * `characteristicValue` for the central role; `readRequest`, `writeRequest`, `centralConnection`,
     * `subscriptionState` and `packetSize` for the peripheral role.
     */
    type: string;
    /** The event's data. Shape depends on `type` — see the table above. */
    payload: any;
}

/** A handler's registration, kept so it can be detached again. */
export interface IBluetoothLowEnergyEventSubscription {
    /** Stops delivering events to this handler. Does not close the channel. */
    off: () => void;
}

/**
 * Receives the Bluetooth events ATBase pushes into this frame and fans them out to handlers.
 *
 * Bluetooth is the wrong shape for the cross-frame transport: a request there is answered exactly
 * once, while a scan, a connection and a subscription each emit indefinitely. So events are not
 * answers to the call that started them. ATBase instead sends each event to this frame as a fresh
 * cross-frame message, the way the interrupt and alert modals call a tool back, and this class is the
 * listener for it.
 *
 * The listener is registered on first subscribe rather than at module load, so a tool that never uses
 * Bluetooth does not register a handler for it.
 */
export class BluetoothLowEnergyEventChannel {
    /** The cross-frame method ATBase delivers Bluetooth events as. Must match the ATBase listener. */
    public static readonly EVENT_METHOD = 'bluetoothLowEnergyEvent';

    private static handlers: Map<string, Set<(event: IBluetoothLowEnergyEvent) => void>> = new Map();
    private static isListenerRegistered = false;

    /**
     * Delivers every event for `namespace` to `handler` until the returned subscription is detached.
     *
     * More than one handler may subscribe to the same namespace; each receives every event.
     */
    public static subscribe(namespace: BluetoothLowEnergyNamespace, handler: (event: IBluetoothLowEnergyEvent) => void): IBluetoothLowEnergyEventSubscription {
        BluetoothLowEnergyEventChannel.registerListener();

        const existing = BluetoothLowEnergyEventChannel.handlers.get(namespace) || new Set<(event: IBluetoothLowEnergyEvent) => void>();
        existing.add(handler);
        BluetoothLowEnergyEventChannel.handlers.set(namespace, existing);

        return {
            off: () => {
                const handlers = BluetoothLowEnergyEventChannel.handlers.get(namespace);
                if (handlers) {
                    handlers.delete(handler);
                }
            },
        };
    }

    /** Detaches every handler for `namespace`. Called when its channel is closed. */
    public static clear(namespace: BluetoothLowEnergyNamespace): void {
        BluetoothLowEnergyEventChannel.handlers.delete(namespace);
    }

    /**
     * Subscribes `handler`, then asks ATBase to start pushing events for `namespace`.
     *
     * The handler is attached before the channel is opened, so an event cannot arrive with nowhere to
     * go. If opening fails the handler is detached again, leaving no subscription behind for a channel
     * that never opened.
     *
     * Open the channel before starting anything that emits — a scan, a connection, a subscription.
     * Events produced while no channel is open are dropped natively rather than queued.
     */
    public static async open(namespace: BluetoothLowEnergyNamespace, handler: (event: IBluetoothLowEnergyEvent) => void): Promise<IBluetoothLowEnergyEventSubscription> {
        const subscription = BluetoothLowEnergyEventChannel.subscribe(namespace, handler);
        try {
            await BluetoothLowEnergyAdapterProvider.get().call(namespace, 'openEventChannel');
        } catch (error) {
            subscription.off();
            throw error;
        }
        return subscription;
    }

    /**
     * Asks ATBase to stop pushing this namespace's events, then detaches every handler.
     *
     * This is a **tool-level** operation, not a component-level one. The channel is a single shared
     * resource per role, so closing it stops the native stream for everything in this frame — a
     * component that merely wants to stop listening must call `off()` on its own subscription
     * instead. Exactly one coordinator per tool should own opening and closing; use
     * {@link subscriberCount} if that coordinator needs to know whether anyone is still listening
     * before it closes.
     *
     * Handlers are detached only after the native close succeeds. Detaching first would leave a
     * window in which the native side is still emitting and every event is discarded for want of a
     * handler; if the close fails the handlers stay attached and keep receiving, which is the
     * accurate reflection of a channel that is still open.
     */
    public static async close(namespace: BluetoothLowEnergyNamespace): Promise<any> {
        const result = await BluetoothLowEnergyAdapterProvider.get().call(namespace, 'closeEventChannel');
        BluetoothLowEnergyEventChannel.clear(namespace);
        return result;
    }

    /**
     * How many handlers are currently attached for `namespace`.
     *
     * This counts subscribers in this frame; it is deliberately not called something like
     * `isChannelOpen`, because nothing on this side can observe whether the native channel is open —
     * only whether anyone here is listening.
     */
    public static subscriberCount(namespace: BluetoothLowEnergyNamespace): number {
        const handlers = BluetoothLowEnergyEventChannel.handlers.get(namespace);
        return handlers ? handlers.size : 0;
    }

    /**
     * Registers the inbound cross-frame listener once.
     *
     * Registering again would simply replace the handler, but the guard keeps the intent explicit:
     * there is one listener for all namespaces, and it dispatches on the namespace in the message.
     */
    private static registerListener(): void {
        if (BluetoothLowEnergyEventChannel.isListenerRegistered) {
            return;
        }
        BluetoothLowEnergyEventChannel.isListenerRegistered = true;

        CrossFrameUtils.registerListener(BluetoothLowEnergyEventChannel.EVENT_METHOD, async (message: any) => {
            BluetoothLowEnergyEventChannel.dispatch(message?.data?.namespace, {
                type: message?.data?.type,
                payload: message?.data?.payload,
            });
            // Resolved immediately: this acknowledges receipt of the event, and ATBase is not waiting
            // on the tool to do anything with it.
            return Promise.resolve();
        });
    }

    /**
     * Hands one event to every handler for its namespace.
     *
     * A handler that throws is logged and skipped rather than allowed to stop the others: one tool
     * component with a bug should not silently cut off the rest of the tool's Bluetooth events.
     */
    private static dispatch(namespace: string, event: IBluetoothLowEnergyEvent): void {
        const handlers = BluetoothLowEnergyEventChannel.handlers.get(namespace);
        if (!handlers || handlers.size === 0) {
            console.debug('Received a Bluetooth event for ' + namespace + ' with no handler subscribed');
            return;
        }
        if (typeof event.type !== 'string' || event.type.length === 0) {
            // The message crosses a frame boundary, so the shape is not guaranteed. Handlers are typed
            // against IBluetoothLowEnergyEvent and switch on `type`, so an absent one is dropped here
            // rather than passed on as an event that satisfies nothing.
            console.debug('Dropping a Bluetooth event for ' + namespace + ' with no usable type');
            return;
        }
        handlers.forEach((handler) => {
            try {
                handler(event);
            } catch (error) {
                console.error('A Bluetooth ' + namespace + ' event handler threw', error);
            }
        });
    }
}
